import {useAppSelector, useBreakPoint, useFeatureToggle} from '@hooks';
import {compareExtrasWorkdayFirstAlphabetical, getExtras, useHomeBuildingId} from '@lib/store';
import {
  ExtrasOverview,
  ConnectionOverview,
  EmployeeCommunicationAnnouncementTile,
  NextWorkdaysSimplifiedSideOverview,
  Tile,
  ExtraServicesOverview,
} from '@organisms';
import {format} from 'date-fns';
import {ConnectionOverview_timeslots} from 'src/components/organisms/home/Connections/ConnectionsOverview_timeslots';

/**
 * This sidebar takes care of its own content. It tries to show content as soon as it's available, but takes care to only _append_ content.
 * This prevents vertical layout shifts that might cause the thing users are looking at / interacting with to move down.
 */
export const Sidebar = ({date}: {date: Date}) => {
  const bp = useBreakPoint();
  const dateKey = format(date, 'yyyy-MM-dd');

  const {
    ExtrasFeature: extrasEnabled,
    EmployeeCommunication: employeeCommunicationEnabled,
    ContextAwareExtras: contextAwareExtrasEnabled,
    TimeslotsApi,
  } = useFeatureToggle();
  const extrasLoading = useAppSelector((state) => state.data.extras.isLoading);

  const relevantExtras = useAppSelector(getExtras)
    .filter((e) => e.type === 'Workday' || e.type === 'UserProfile')
    // Note: should we respect server-side ordering instead?
    .sort(compareExtrasWorkdayFirstAlphabetical);

  // On mobile, connections section gets its own tile
  // On wider breakpoints, only show it once extras have loaded
  const showConnections = bp !== 'small' && !extrasLoading;
  const buildingId = useHomeBuildingId(dateKey);

  return (
    <>
      {employeeCommunicationEnabled && <EmployeeCommunicationAnnouncementTile />}

      <NextWorkdaysSimplifiedSideOverview referenceDate={date} />

      {!extrasEnabled ? null : contextAwareExtrasEnabled ? (
        <ExtraServicesOverview referenceDate={date} />
      ) : (
        <ExtrasOverview
          extras={relevantExtras}
          loading={extrasLoading}
        />
      )}
      {showConnections ? (
        <Tile>
          {TimeslotsApi ? (
            <ConnectionOverview_timeslots
              dateKey={dateKey}
              buildingId={buildingId}
            />
          ) : (
            <ConnectionOverview dateKey={dateKey} />
          )}
        </Tile>
      ) : null}
    </>
  );
};
